/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
export enum TransactionOperation {
  CREATE = 'create',
  UPDATE = 'update',
  UPSERT = 'upsert',
  DELETE = 'delete'
}

type TransactionParentActionReference = {
  action: string;
};
type TransactionParentEntityReference = {
  entity: string;
};
type TransactionParentReference =
  | TransactionParentActionReference
  | TransactionParentEntityReference;

type TransactionOption = {
  if_not_modified_since: string;
};

type TransactionBase = {
  operation: TransactionOperation;
  type: string;
  target_reference?: string;
  parent_references?: TransactionParentReference;
  options?: TransactionOption;
};

export type Transaction = TransactionBase &
  (
    | {
        operation: TransactionOperation.CREATE;
        payload: any;
      }
    | {
        operation: TransactionOperation.UPDATE;
        payload: any;
      }
    | {
        operation: TransactionOperation.UPSERT;
        payload: any;
      }
    | {
        operation: TransactionOperation.DELETE;
      }
  );

export const composeTransaction = (
  type: string,
  operation: TransactionOperation,
  payload: any,
  target_reference?: string,
  parent_references?: TransactionParentReference,
  options?: TransactionOption
): Transaction => {
  const transaction = {
    type,
    operation,
    payload,
    target_reference,
    parent_references,
    options
  };
  if (!target_reference) {
    delete transaction.target_reference;
  }
  if (!parent_references) {
    delete transaction.parent_references;
  }
  if (!options) {
    delete transaction.options;
  }
  return transaction;
};

export const composeCreateTransaction = (
  type: string,
  payload: any,
  target_reference?: string,
  parent_references?: TransactionParentReference,
  options?: TransactionOption
) => {
  return composeTransaction(
    type,
    TransactionOperation.CREATE,
    payload,
    target_reference,
    parent_references,
    options
  );
};

export const composeCreateIntentTransaction = (
  payload: any,
  target_reference?: string
) => {
  return composeCreateTransaction('intent', payload, target_reference);
};

export const composeCreateEntityTransaction = (
  payload: any,
  target_reference?: string
) => {
  return composeCreateTransaction('entity', payload, target_reference);
};

export const composeCreateVariableTransaction = (
  payload: any,
  target_reference: string,
  parent_references?: TransactionParentReference
) => {
  return composeCreateTransaction(
    'variable',
    payload,
    target_reference,
    parent_references
  );
};

export const composeCreateActionTransaction = (
  payload: any,
  target_reference?: string,
  parent_references?: TransactionParentReference,
  options?: TransactionOption
) => {
  return composeCreateTransaction(
    'action',
    payload,
    target_reference,
    parent_references,
    options
  );
};

// We should abstract more functions for transaction api gradually
// as we refactor in the future
